import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Card, Button } from '../components/ui';
import Header from './Header';
import Footer from './Footer';
import aboutCampus from '../assets/images/about-hero.jpg';
import './AboutPage.css';

const pillars = [
    {
        title: 'Consulting rooted in adoption',
        body: 'Change management consulting is only as good as what happens Monday morning. We design programmes, governance, and adoption so new ways of working show up in workflows and metrics—not only in workshops.',
    },
    {
        title: 'Capital with judgment',
        body: 'Angel investing and private equity sit alongside our consulting and technology work: we invest where our operating experience and network add more than capital, with transparency on milestones and risk.',
    },
    {
        title: 'Technology that earns a seat at the table',
        body: 'We deliver automation and AI solutions and develop analytics tools that leadership actually uses—scoped to decisions, integrated with your data reality, and sustained with enablement your teams own.',
    },
];

const AboutPage = () => {
    const navigate = useNavigate();

    return (
        <div className="about-page">
            <Header variant="marketing" />
            <Section variant="large" className="about-hero anchor-target">
                <div className="about-hero-inner">
                    <motion.p
                        className="about-eyebrow"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45 }}
                    >
                        About MotionSpace
                    </motion.p>
                    <motion.h1
                        className="about-title"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                    >
                        Consulting, capital, and technology—for organisations that have to move for real.
                    </motion.h1>
                    <motion.p
                        className="about-lede"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        MotionSpace helps clients navigate complexity with change management consulting, with angel and
                        private-equity capital when investment and governance belong in the same conversation, and with
                        automation, AI, and analytics we deliver and build so decisions are faster and change lasts.
                    </motion.p>
                    <motion.div
                        className="about-hero-cta"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                    >
                        <Button variant="primary" size="lg" onClick={() => navigate('/contact')}>
                            Start a conversation
                        </Button>
                        <Button variant="outline" size="lg" onClick={() => navigate('/services')}>
                            View services
                        </Button>
                    </motion.div>
                </div>
            </Section>

            <Section variant="default" className="about-story">
                <div className="about-story-grid">
                    <motion.div
                        className="about-story-copy"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.55 }}
                    >
                        <h2 className="about-section-title">What we care about</h2>
                        <p className="about-body">
                            We work with leadership and delivery teams when the stakes are high: transformation,
                            integration, new propositions, or a reset in how the organisation uses data and technology.
                            Our consulting practice focuses on clarity, adoption, and governance that teams can sustain.
                        </p>
                        <p className="about-body">
                            Where capital can accelerate the same agenda, we engage as angel or private-equity partners
                            with disciplined milestones—not spectacle. And we put automation, AI, and analytics in service
                            of those outcomes: tools and products that make performance and risk visible when you need
                            to act.
                        </p>
                    </motion.div>
                    <motion.div
                        className="about-story-visual-col"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.55, delay: 0.08 }}
                    >
                        <img
                            src={aboutCampus}
                            alt="Glass campus facade and pedestrian plaza in daylight"
                            className="about-story-image"
                        />
                        <Card variant="glass" className="about-highlight-card">
                            <h3 className="about-highlight-title">Based in The Hague</h3>
                            <p className="about-highlight-body">
                                MotionSpace BV works with clients across sectors, combining local presence with remote
                                collaboration. Reach us through the contact page for mandates that span consulting,
                                capital, and technology—or any one of them.
                            </p>
                            <Link to="/contact" className="about-inline-link">
                                Contact details →
                            </Link>
                        </Card>
                    </motion.div>
                </div>
            </Section>

            <Section variant="default" className="about-pillars">
                <motion.h2
                    className="about-pillars-heading"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5 }}
                >
                    How we show up
                </motion.h2>
                <div className="about-pillars-grid">
                    {pillars.map((pillar, index) => (
                        <motion.div
                            key={pillar.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.5, delay: index * 0.08 }}
                        >
                            <Card variant="interactive" className="about-pillar-card">
                                <h3 className="about-pillar-title">{pillar.title}</h3>
                                <p className="about-pillar-body">{pillar.body}</p>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </Section>

            <Footer />
        </div>
    );
};

export default AboutPage;
